type BugsnagStackFrame = {
  file?: string;
};

type BugsnagErrorEvent = {
  unhandled?: boolean;
  errors?: { stacktrace?: BugsnagStackFrame[] }[];
};

type BuildIdentifiers = {
  appName: string | undefined;
  buildHash: string | undefined;
  currentPath?: string;
};

// Bugsnag substitutes a placeholder when it cannot attribute a frame to a file.
// The notifier writes 'global code' itself; 'unknown file' and '[native code]'
// come back from the backend. All three are truthy yet identify nothing, so
// counting them as foreign would discard our own errors.
const unattributableFiles = new Set(['global code', 'unknown file', '[native code]']);

const isAttributable = (file: string | undefined): file is string => {
  if (!file) {
    return false;
  }

  return !unattributableFiles.has(file.trim().toLowerCase());
};

export const isThirdPartyScriptError = (
  event: BugsnagErrorEvent,
  { appName, buildHash, currentPath }: BuildIdentifiers
): boolean => {
  // Fail open: an event we reported ourselves, or one we cannot attribute to a
  // file, is always worth keeping. Only a stack that is entirely foreign is noise.
  if (!event.unhandled || !appName || !buildHash) {
    return false;
  }

  const ownAssetPath = `/${appName}/${buildHash}/`;

  // The marker is only meaningful when our own code is served from that path.
  // A root-served bundle such as widget-loader never matches it, so arming the
  // filter there would discard every unhandled error instead of just foreign ones.
  if (!currentPath?.includes(ownAssetPath)) {
    return false;
  }

  const files = (event.errors ?? [])
    .flatMap((error) => error.stacktrace ?? [])
    .map((frame) => frame.file)
    .filter(isAttributable);

  if (files.length === 0) {
    return false;
  }

  return !files.some((file) => file.includes(ownAssetPath));
};
